/**
 * Centralized SEO meta helpers.
 *
 * Every title and description across the site should flow through these
 * functions so character limits are enforced in one place rather than
 * being eyeballed per-route.
 *
 * Title limit:  60 characters (Google truncates at ~60)
 * Description:  155 characters (Google truncates at ~155-160)
 */

export const SITE = "https://heallexa.com";
export const BRAND = "Heallexa";
const MAX_TITLE = 60;
const MAX_DESC = 155;

// ── Credential deduplication ─────────────────────────────────────────────────
//
// The federal NPI registry pre-bakes credentials into the provider name field
// at import time (e.g. "Jane Smith, DMD"). When the UI then appends the
// credentials column a second time the result is "Jane Smith, DMD, DMD".
// This appears across every title, description, H1, and JSON-LD name field
// on 6.3 million provider pages and triggers Google's duplicate-token spam
// signal.
//
// stripTerminalCredentials() removes any credential suffix already embedded
// at the END of the name string. It matches against a sorted-longest-first
// token list so multi-token credentials like "PMHNP_BC" take precedence over
// the shorter "PMHNP".

const CREDENTIAL_TOKENS = [
  // Multi-part (must come first — longest-first ordering)
  "PMHNP_BC", "PMHNP-BC", "CCC_SLP", "CCC-SLP", "DTR_L", "FNP_BC", "FNP-BC",
  "FNP_C", "FNP-C", "PA_C", "PA-C", "NP_C", "NP-C", "APRN_BC", "APRN-BC",
  "AGACNP_BC", "AGACNP-BC", "ACNPC_AG", "ACNPC-AG", "CPNP_AC", "CPNP-AC",
  "CPNP_PC", "CPNP-PC", "LCSW_C", "LCSW-C", "LCSW_R", "LCSW-R",
  "DNP_FNP", "GNP_C", "GNP-C", "MSN_RN", "ANP_BC", "ANP-BC",
  // Single tokens — sorted longest-first within this group
  "PMHNP", "PharmD", "LICSW", "LCSW", "LMFT", "LMHC", "LPCC", "LPC", "LMSW",
  "APRN", "BSN", "MSN", "PsyD", "PSYD", "EdD", "EDD", "FNP", "DNP", "DPT",
  "AuD", "AUD", "RDN", "FACS", "FAAP", "FACOG", "FACC", "MBBS",
  "MD", "DO", "DDS", "DMD", "OD", "RPh", "PhD", "PHD", "RN", "NP", "PA", "PT",
  "QT", "OTR", "SLP", "DC", "LAc", "LAC", "LMT", "RD", "CPC", "LND", "CPC",
  "BCBA", "LBA", "ATC", "LAT", "CNM", "WHNP", "FNP", "CRNA", "CRNP",
  "ARNP", "APN", "CNP", "CNS", "GNP", "NNP", "PNP",
] as const;

// Escape any regex-special characters in a token string
function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Pre-build regex: matches one or more credential tokens at the end of the
// string, preceded by common separators (comma, period, slash, whitespace).
// Applied iteratively until no further match is found.
const CREDENTIAL_RE = new RegExp(
  `(?:[\\s,./;]+(?:${CREDENTIAL_TOKENS.map(escapeRe).join("|")}))+\\s*$`,
  "i",
);

/**
 * Strip any credential suffixes already embedded at the end of a provider
 * name string. Runs iteratively so stacked credentials ("Smith, MD, FACS")
 * are all removed in one call.
 *
 * Examples:
 *   "Jane Smith, DMD, DMD"    → "Jane Smith"
 *   "Dr. John Doe, MD"        → "Dr. John Doe"
 *   "JANE SMITH, MD, FACS"    → "JANE SMITH"
 *   "MacDonald, MD"           → "MacDonald"   (mid-name MD in MacDonald preserved)
 *   "Smith Family Dental, PC" → "Smith Family Dental"
 */
export function stripTerminalCredentials(name: string): string {
  if (!name) return name;
  let s = name.trim();
  // Iteratively strip until the regex finds nothing new
  for (let i = 0; i < 10; i++) {
    const stripped = s.replace(CREDENTIAL_RE, "").trim().replace(/[,;./\s]+$/, "").trim();
    if (stripped === s) break;
    s = stripped;
  }
  return s;
}

// ── Generic helpers ──────────────────────────────────────────

/** Truncate text at a sentence or word boundary. */
export function clamp(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastPeriod = cut.lastIndexOf(". ");
  if (lastPeriod > max * 0.5) return cut.slice(0, lastPeriod + 1);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace > max * 0.6) return cut.slice(0, lastSpace);
  return cut.trimEnd();
}

/** Enforce title limit (60 chars). */
export function safeTitle(text: string): string {
  return clamp(text, MAX_TITLE);
}

/** Enforce description limit (155 chars). */
export function safeDesc(text: string): string {
  return clamp(text, MAX_DESC);
}

// ── Provider templates ───────────────────────────────────────

interface ProviderMeta {
  name: string;
  credentials?: string | null;
  specialty: string;
  city: string;
  state?: string | null;
  address?: string | null;
  zip?: string | null;
  phone?: string | null;
}

/**
 * Provider page title - progressive truncation to stay under 60ch.
 *
 * Strips any credentials already embedded in the name field (NPI data
 * pre-bakes them), then re-appends from the credentials column exactly
 * once to prevent "Jane Smith, DMD, DMD" duplication.
 *
 * Priority order:
 *  1. "{Name}, {Creds} | {Specialty} in {City}, {State} | Heallexa"
 *  2. "{Name} | {Specialty} in {City}, {State} | Heallexa"     (drop creds)
 *  3. "{Name} | {Specialty} in {City}, {State}"                (drop brand)
 */
export function providerTitle(p: ProviderMeta): string {
  // Strip NPI-embedded credentials before re-adding from the credentials column
  const cleanName = stripTerminalCredentials(p.name);
  const nameWithCreds = p.credentials ? `${cleanName}, ${p.credentials}` : cleanName;
  const loc = [p.city, p.state].filter(Boolean).join(", ");

  const full = `${nameWithCreds} | ${p.specialty} in ${loc} | ${BRAND}`;
  if (full.length <= MAX_TITLE) return full;

  const noCreds = `${cleanName} | ${p.specialty} in ${loc} | ${BRAND}`;
  if (noCreds.length <= MAX_TITLE) return noCreds;

  return clamp(`${cleanName} | ${p.specialty} in ${loc}`, MAX_TITLE);
}

/**
 * Provider page description - rich, unique per provider, under 155ch.
 *
 * Strips NPI-embedded credentials before re-adding from the credentials
 * column exactly once. Progressively drops parts when the combined string
 * exceeds the limit.
 */
export function providerDescription(p: ProviderMeta): string {
  const cleanName = stripTerminalCredentials(p.name);
  const creds = p.credentials ? `, ${p.credentials}` : "";
  const loc = [p.city, p.state].filter(Boolean).join(", ");
  const addr = p.address ? `${p.address}, ${loc}` : loc;
  const zip = p.zip ? ` ${p.zip}` : "";
  const phone = p.phone ? ` Call ${p.phone}.` : "";

  const v1 = `${cleanName}${creds} - ${p.specialty} at ${addr}${zip}.${phone} Book on ${BRAND}.`;
  if (v1.length <= MAX_DESC) return v1;

  const v2 = `${cleanName}${creds} - ${p.specialty} at ${addr}.${phone} Book on ${BRAND}.`;
  if (v2.length <= MAX_DESC) return v2;

  const v3 = `${cleanName}${creds} - ${p.specialty} at ${addr}.${phone}`;
  if (v3.length <= MAX_DESC) return v3;

  const v4 = `${cleanName} - ${p.specialty} at ${addr}.${phone}`;
  if (v4.length <= MAX_DESC) return v4;

  const v5 = `${cleanName} - ${p.specialty} in ${loc}.${phone} Book on ${BRAND}.`;
  if (v5.length <= MAX_DESC) return v5;

  return clamp(`${cleanName} - ${p.specialty} in ${loc}.${phone}`, MAX_DESC);
}

// ── Static page templates ────────────────────────────────────

/**
 * Build a branded title: "{PageTitle} | Heallexa".
 * If that exceeds 60ch, drops the brand suffix.
 */
export function brandedTitle(pageTitle: string): string {
  const full = `${pageTitle} | ${BRAND}`;
  return full.length <= MAX_TITLE ? full : clamp(pageTitle, MAX_TITLE);
}

// ── Specialty + City templates ───────────────────────────────

export function specialtyCityTitle(specialty: string, city: string, count?: number, year?: number): string {
  const yr = year ?? new Date().getFullYear();
  const ct = count ? `${count} ` : "";
  return safeTitle(`${ct}Best ${specialty} in ${city} (${yr}) | ${BRAND}`);
}

export function specialtyCityDescription(specialty: string, city: string, count?: number): string {
  const ct = count ? `${count}+ ` : "";
  return safeDesc(
    `Find top-rated ${specialty.toLowerCase()} in ${city}. Compare ${ct}providers, read reviews, check insurance, and book - all free on ${BRAND}.`,
  );
}

// ── Insurance templates ──────────────────────────────────────

export function insuranceTitle(insuranceName: string): string {
  return safeTitle(`Doctors Accepting ${insuranceName} | ${BRAND}`);
}

export function insuranceDescription(insuranceName: string): string {
  return safeDesc(
    `Find doctors, dentists, and specialists that accept ${insuranceName}. Search by specialty and city on ${BRAND}.`,
  );
}

// ── Location templates ───────────────────────────────────────

export function stateTitle(stateName: string): string {
  return safeTitle(`Healthcare Providers in ${stateName} | ${BRAND}`);
}

export function cityTitle(city: string, stateCode: string): string {
  return safeTitle(`Healthcare Providers in ${city}, ${stateCode} | ${BRAND}`);
}

// ── Press / Article templates ────────────────────────────────

export function pressTitle(headline: string): string {
  return safeTitle(`${headline} | ${BRAND}`);
}

export function pressDescription(summary: string): string {
  return safeDesc(summary);
}
